import { NavLink, Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Home,
  Sun,
  TrendingUp,
  BookOpen,
  Sparkles,
  User as UserIcon,
  Shield,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Plus,
  Send,
} from "lucide-react";

// Telegram deep-link — used by every "Open Telegram" CTA in the shell.
// VITE_BOT_USERNAME is baked at build time; the literal fallback keeps
// the link useful even if the env var is missing.
const BOT_URL = `https://t.me/${import.meta.env.VITE_BOT_USERNAME || "ilmli_agent_bot"}`;
import Logo from "../components/common/Logo";
import { api, type Profile, type LanguageOption } from "../api";
import { signOut } from "../amplify";
import { cn } from "../lib/format";

const NAV: Array<{ to: string; label: string; icon: typeof Home; admin?: boolean }> = [
  { to: "/app/today", label: "Today", icon: Sun },
  { to: "/app/journey", label: "Journey", icon: TrendingUp },
  { to: "/app/vocabulary", label: "Vocabulary", icon: BookOpen },
  { to: "/app/personas", label: "Personas", icon: Sparkles },
  { to: "/app/profile", label: "Settings", icon: UserIcon },
  { to: "/app/admin", label: "Admin", icon: Shield, admin: true },
];

export default function AppShell() {
  const nav = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [catalog, setCatalog] = useState<LanguageOption[]>([]);
  const langRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api
      .getMe()
      .then((p) => {
        setProfile(p);
        if (!p.onboarding_complete) nav("/onboarding", { replace: true });
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Failed to load"));
    api.getLanguages().then((r) => setCatalog(r.languages || [])).catch(() => {});
  }, [nav]);

  // Close the language dropdown when clicking outside.
  useEffect(() => {
    if (!langOpen) return;
    function onDoc(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [langOpen]);

  async function onSignOut() {
    await signOut();
    nav("/login", { replace: true });
  }

  // "Open Telegram" — if THIS account is already the linked one, jump
  // straight to the bot. Otherwise, mint a fresh link code and use the
  // deep-link with `?start=<code>` so the bot re-points the chat to the
  // currently signed-in account on first message. Pre-opens a blank tab
  // synchronously to satisfy popup blockers; the await happens after.
  async function onOpenTelegram(e: React.MouseEvent) {
    e.preventDefault();
    const blankTab = window.open("about:blank", "_blank");
    try {
      let url = BOT_URL;
      if (!profile?.activated) {
        const { deep_link } = await api.createLinkCode();
        if (deep_link) url = deep_link;
      }
      if (blankTab) blankTab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      if (blankTab) blankTab.close();
      setErr(err instanceof Error ? err.message : "Couldn't open Telegram — try again.");
    }
  }

  async function pickLanguage(code: string) {
    if (!profile || code === profile.active_language) {
      setLangOpen(false);
      return;
    }
    setLangOpen(false);
    try {
      const updated = await api.updateMe({ active_language: code });
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "switch failed");
    }
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card max-w-md text-center">
          <p className="text-terracotta">{err}</p>
        </div>
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Logo size="lg" />
      </div>
    );
  }

  const items = NAV.filter((i) => !i.admin || profile.is_admin);
  const owned = profile.languages || [profile.active_language];
  const nameFor = (code: string) => catalog.find((l) => l.code === code)?.name ?? code.toUpperCase();
  const initial = (profile.first_name || profile.email || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen flex relative">
      <a href="#main" className="skip-link">Skip to main content</a>
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed md:static inset-y-0 left-0 z-30 w-64 bg-paper-soft/80 backdrop-blur-md border-r border-[rgba(42,36,26,0.12)] flex flex-col transition-transform md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="px-5 pt-5 pb-3">
          <Logo size="md" />
        </div>

        {/* Language switcher */}
        <div ref={langRef} className="relative px-3">
          <button
            onClick={() => setLangOpen((o) => !o)}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-paper border border-[rgba(42,36,26,0.18)] hover:border-terracotta/50 transition-colors text-left"
            aria-haspopup="listbox"
            aria-expanded={langOpen}
          >
            <div className="flex-1 min-w-0">
              <div className="font-serif italic text-base leading-tight truncate">
                {nameFor(profile.active_language)}
              </div>
              <div className="font-mono text-[10px] text-ink-faint mt-0.5 tracking-wide">
                {profile.cefr_level || "—"} · active
              </div>
            </div>
            <ChevronDown
              size={14}
              className={cn(
                "text-ink-faint shrink-0 transition-transform",
                langOpen && "rotate-180",
              )}
            />
          </button>
          {langOpen && (
            <div
              className="absolute left-3 right-3 top-full mt-1.5 bg-paper border border-[rgba(42,36,26,0.18)] rounded-md shadow-lg overflow-hidden z-40"
              role="listbox"
            >
              {owned.map((code) => {
                const isActive = code === profile.active_language;
                return (
                  <button
                    key={code}
                    onClick={() => pickLanguage(code)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-terracotta/8 transition-colors",
                      isActive && "bg-terracotta/8",
                    )}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span className="font-serif italic text-[15px] flex-1">{nameFor(code)}</span>
                    {isActive && (
                      <span className="font-mono text-[10px] text-terracotta tracking-wide">active</span>
                    )}
                  </button>
                );
              })}
              <NavLink
                to="/app/profile"
                onClick={() => setLangOpen(false)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-terracotta text-[13px] border-t border-[rgba(42,36,26,0.12)] hover:bg-terracotta/8 transition-colors"
              >
                <Plus size={12} />
                Add a language
              </NavLink>
            </div>
          )}
        </div>

        <nav className="flex-1 px-3 py-5 space-y-0.5">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 pl-3 pr-3 py-2 text-sm transition-colors border-l-2",
                  isActive
                    ? "border-terracotta bg-terracotta-tint text-terracotta font-medium"
                    : "border-transparent text-ink-soft hover:text-terracotta hover:bg-terracotta-tint/60",
                )
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer: identity + Telegram status */}
        <div className="p-3 border-t border-[rgba(42,36,26,0.12)]">
          <div className="flex items-center gap-2.5 px-1 py-1">
            <div className="w-7 h-7 rounded-full bg-terracotta text-paper flex items-center justify-center text-xs font-medium shrink-0">
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium leading-tight truncate">
                {profile.first_name || profile.email}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full shrink-0",
                    profile.activated ? "bg-[#386641]" : "bg-ink-faint/60",
                  )}
                />
                <span className="font-mono text-[10px] text-ink-faint truncate">
                  {profile.activated ? "Telegram connected" : "Telegram not linked"}
                </span>
              </div>
            </div>
          </div>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onOpenTelegram}
            className="mt-2 w-full flex items-center gap-3 px-3 py-2 rounded text-sm bg-terracotta text-paper hover:bg-terracotta-soft transition-colors"
          >
            <Send size={15} />
            Open Telegram
          </a>
          <button
            onClick={onSignOut}
            className="mt-1.5 w-full flex items-center gap-3 px-3 py-2 rounded text-sm text-ink-soft hover:bg-[rgba(184,71,44,0.08)] hover:text-terracotta transition-colors"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-20 bg-ink/30 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        <header className="md:hidden h-16 px-4 flex items-center justify-between border-b border-[rgba(42,36,26,0.12)] gap-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2"
            aria-label="Open navigation"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Logo size="sm" />
          {/* Always-visible Open-Telegram button — the bot lives there,
              and people who land on /today empty or who deleted the
              chat thread need a one-tap way back. */}
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onOpenTelegram}
            className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-terracotta text-paper hover:bg-terracotta-soft transition-colors"
            aria-label="Open Telegram chat"
          >
            <Send size={12} />
            <span className="font-mono text-[10px] tracking-wide">CHAT</span>
          </a>
          {/* Active language quick-switcher: tap to jump to the sidebar (which scrolls focus to the dropdown). */}
          <button
            onClick={() => {
              setMobileOpen(true);
              setLangOpen(true);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[rgba(42,36,26,0.18)] bg-paper hover:border-terracotta transition-colors"
            aria-label={`Switch language — currently ${nameFor(profile.active_language)}`}
          >
            <span className="font-mono text-[10px] tracking-wide text-terracotta">
              {profile.active_language?.toUpperCase() || "—"}
            </span>
            <ChevronDown size={11} className="text-ink-faint" />
          </button>
        </header>
        <main id="main" className="flex-1 overflow-x-hidden">
          {!profile.activated && (
            <div className="bg-terracotta/8 border-b border-terracotta/30 px-6 md:px-10 py-3">
              <div className="max-w-[1200px] mx-auto flex items-center gap-3 flex-wrap text-[13px]">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-terracotta">
                  warning · telegram
                </span>
                <span className="font-serif italic text-ink-soft">
                  Your tutor can't reach you — Telegram disconnected. Reconnect and we'll
                  pick up exactly where we left off.
                </span>
                <a
                  href={BOT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onOpenTelegram}
                  className="ml-auto font-mono text-[12px] text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta"
                >
                  Reconnect →
                </a>
              </div>
            </div>
          )}
          <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14">
            <Outlet context={{ profile, setProfile }} />
          </div>
        </main>
      </div>
    </div>
  );
}

export function useProfile(): { profile: Profile; setProfile: (p: Profile) => void } {
  return useOutletContext<{ profile: Profile; setProfile: (p: Profile) => void }>();
}
